import React from 'react';
import { getAllBadges, getBadgesByType, BADGE_TYPES, BADGE_RARITY } from '../../../data/badgeDefinitions';

const BadgeDisplay = ({ badges = [], stats = {} }) => {
  const allBadges = getAllBadges();
  const unlockedBadgeIds = badges || [];

  const getBadgeProgress = (badge) => {
    const { requirement } = badge;
    const badgeStats = stats.stats || {};

    let current = 0;
    let target = requirement.value;

    switch (requirement.type) {
      case 'shifts_completed':
        current = badgeStats.totalShifts || 0;
        break;
      case 'inspections_completed':
        current = badgeStats.totalInspections || 0;
        break;
      case 'borrows_completed':
        current = badgeStats.totalBorrows || 0;
        break;
      case 'repairs_completed':
        current = badgeStats.totalRepairs || 0;
        break;
      case 'perfect_attendance_days':
        current = badgeStats.perfectAttendanceDays || 0;
        break;
      case 'level':
        current = stats.level || 1;
        break;
      default:
        current = 0;
    }

    return {
      current: Math.min(current, target),
      target,
      percent: Math.min((current / target) * 100, 100)
    };
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case BADGE_RARITY.COMMON:
        return 'border-gray-300 bg-gray-50';
      case BADGE_RARITY.RARE:
        return 'border-blue-300 bg-blue-50';
      case BADGE_RARITY.EPIC:
        return 'border-purple-300 bg-purple-50';
      case BADGE_RARITY.LEGENDARY:
        return 'border-yellow-300 bg-yellow-50';
      default:
        return 'border-gray-300 bg-gray-50';
    }
  };

  const badgesByType = {
    [BADGE_TYPES.SHIFT]: getBadgesByType(BADGE_TYPES.SHIFT),
    [BADGE_TYPES.EQUIPMENT]: getBadgesByType(BADGE_TYPES.EQUIPMENT),
    [BADGE_TYPES.TEAM]: getBadgesByType(BADGE_TYPES.TEAM),
    [BADGE_TYPES.SOCIAL]: getBadgesByType(BADGE_TYPES.SOCIAL),
    [BADGE_TYPES.LEVEL]: getBadgesByType(BADGE_TYPES.LEVEL)
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800">🏅 Badges</h3>
          <div className="text-sm text-gray-600">
            ได้รับแล้ว: <span className="font-bold text-blue-600">{unlockedBadgeIds.length}</span> / {allBadges.length}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(badgesByType).map(([type, typeBadges]) => {
            const unlocked = typeBadges.filter(b => unlockedBadgeIds.includes(b.badgeId)).length;
            return (
              <div key={type} className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl mb-1">{typeBadges[0]?.icon || '🏅'}</div>
                <div className="text-sm font-medium text-gray-700">{type}</div>
                <div className="text-xs text-gray-600">{unlocked}/{typeBadges.length}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Badges by Type */}
      {Object.entries(badgesByType).map(([type, typeBadges]) => (
        <div key={type} className="bg-white rounded-lg shadow-md p-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">
            {type === BADGE_TYPES.SHIFT && '📅 Shift Badges'}
            {type === BADGE_TYPES.EQUIPMENT && '🔧 Equipment Badges'}
            {type === BADGE_TYPES.TEAM && '👥 Team Badges'}
            {type === BADGE_TYPES.SOCIAL && '🎁 Social Badges'}
            {type === BADGE_TYPES.LEVEL && '👑 Level Badges'}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {typeBadges.map((badge) => {
              const isUnlocked = unlockedBadgeIds.includes(badge.badgeId);
              const progress = getBadgeProgress(badge);
              const rarityColor = getRarityColor(badge.rarity);

              return (
                <div
                  key={badge.badgeId}
                  className={`border-2 rounded-lg p-4 transition-all ${
                    isUnlocked
                      ? `${rarityColor} opacity-100`
                      : 'border-gray-200 bg-gray-50 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-4xl">{badge.icon}</div>
                    {isUnlocked && (
                      <span className="text-xs bg-green-500 text-white px-2 py-1 rounded">ได้รับแล้ว</span>
                    )}
                  </div>
                  <h5 className="font-semibold text-gray-800 mb-1">{badge.nameThai}</h5>
                  <p className="text-sm text-gray-600 mb-3">{badge.description}</p>
                  
                  {/* Progress */}
                  {!isUnlocked && (
                    <div className="mb-2">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>ความคืบหน้า</span>
                        <span>{progress.current}/{progress.target}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${progress.percent}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Rewards */}
                  {isUnlocked && (
                    <div className="text-xs text-gray-600">
                      <div>+{badge.reward.points} Points</div>
                      <div>+{badge.reward.xp} XP</div>
                      <div>+{badge.reward.coins} Coins</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BadgeDisplay;

